import React, { useEffect, useMemo, useState } from 'react';
import './reviews-table.css';

/**
 * Props:
 *  - show: boolean
 *  - onClose: () => void
 *  - role: 'employee' | 'manager'
 *  - kpa: { id, name, weight?, description?, alignment?, timeframe?: { name? } }
 *  - review: {
 *      id,
 *      employee_rating_id?, employee_description?, employee_comment?,
 *      manager_rating_id?,  manager_description?,  manager_comment?
 *    }
 *  - onSave: ({ id, value }) => Promise<void>
 *  - loadKpaDetails?: (kpaId: number) => Promise<object|null>   // optional
 *  - contractId?: number                                        // optional, passed through if your loader needs it
 */
export default function KPAReviewSummaryModal({
                                                  show,
                                                  onClose,
                                                  role = 'employee',
                                                  kpa,
                                                  review,
                                                  onSave,
                                                  loadKpaDetails,
                                              }) {
    const [busy, setBusy] = useState(false);
    const [details, setDetails] = useState(kpa || {});
    const [empComment, setEmpComment] = useState(review?.employee_comment || '');
    const [mgrComment, setMgrComment] = useState(review?.manager_comment || '');

    // Keep comments in sync if a different review is opened
    useEffect(() => {
        setEmpComment(review?.employee_comment || '');
        setMgrComment(review?.manager_comment || '');
    }, [review?.id]);

    // Lazy-enrich KPA details for modal if loader is provided and fields are missing
    useEffect(() => {
        let alive = true;

        const missing =
            !details?.description ||
            !details?.alignment ||
            !(details?.timeframe && details.timeframe.name);

        if (show && kpa?.id && loadKpaDetails && missing) {
            (async () => {
                try {
                    const extra = await loadKpaDetails(kpa.id);
                    if (!alive) return;
                    if (extra) setDetails(prev => ({ ...prev, ...extra }));
                } catch {
                    // ignore; show what we have
                }
            })();
        }

        return () => { alive = false; };
    }, [show, kpa?.id, loadKpaDetails]);

    const isEmployee = role === 'employee';
    const isManager = role === 'manager';

    const canEditEmp = isEmployee && !!review?.id;
    const canEditMgr = isManager && !!review?.id;

    const save = async () => {
        if (!review?.id) return;
        setBusy(true);
        try {
            const value = isEmployee ? empComment : mgrComment;
            await onSave({ id: review.id, value });
        } finally {
            setBusy(false);
        }
    };

    // Bootstrap-ish modal shell (no dependency on JS plugins)
    if (!show) return null;

    return (
        <>
            <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
                <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
                    <div className="modal-content">

                        <div className="modal-header">
                            <h5 className="modal-title">KPA Details</h5>
                            <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
                        </div>

                        <div className="modal-body">
                            {/* Top: Employee name not provided here; keep to KPA details */}
                            <div className="rt-modal-grid">
                                <div className="rt-modal-label">Name</div>
                                <div className="rt-modal-value">{details?.name ?? '—'}</div>

                                <div className="rt-modal-label">Description</div>
                                <div className="rt-modal-value">{details?.description ?? '—'}</div>

                                <div className="rt-modal-label">Weight</div>
                                <div className="rt-modal-value">{details?.weight ?? '—'}</div>

                                <div className="rt-modal-label">Timeframe</div>
                                <div className="rt-modal-value">{details?.timeframe?.name ?? '—'}</div>

                                <div className="rt-modal-label">Alignment</div>
                                <div className="rt-modal-value">{details?.alignment ?? '—'}</div>
                            </div>

                            <hr className="my-3" />

                            <div className="row g-3">
                                <div className="col-md-6">
                                    <div className="fw-semibold mb-1">Employee Rating</div>
                                    <div className="small text-muted">ID / Description</div>
                                    <div className="form-control rt-control-readonly">
                                        {review?.employee_rating_id ?? '—'} / {review?.employee_description ?? '—'}
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="fw-semibold mb-1">Manager Rating</div>
                                    <div className="small text-muted">ID / Description</div>
                                    <div className="form-control rt-control-readonly">
                                        {review?.manager_rating_id ?? '—'} / {review?.manager_description ?? '—'}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-3">
                                <div className="fw-semibold mb-1">Employee Comment</div>
                                <textarea
                                    className="form-control"
                                    rows={3}
                                    value={empComment}
                                    onChange={(e) => setEmpComment(e.target.value)}
                                    disabled={!canEditEmp}
                                    placeholder={canEditEmp ? 'Type your comment...' : '—'}
                                />
                            </div>

                            <div className="mt-3">
                                <div className="fw-semibold mb-1">Manager Comment</div>
                                <textarea
                                    className="form-control"
                                    rows={3}
                                    value={mgrComment}
                                    onChange={(e) => setMgrComment(e.target.value)}
                                    disabled={!canEditMgr}
                                    placeholder={canEditMgr ? 'Type your comment...' : '—'}
                                />
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-success"
                                onClick={save}
                                disabled={busy || !review?.id || (!canEditEmp && !canEditMgr)}
                            >
                                {busy ? 'Saving…' : 'Save'}
                            </button>
                        </div>

                    </div>
                </div>
            </div>
            <div className="modal-backdrop fade show" />
        </>
    );
}